import { skipToken } from "@reduxjs/toolkit/query";
import {
  allCallingWeekdays,
  compareStaticSectionsAsStored,
  getUnpublishableSettingsReason,
  isStaticTeammateAgentIdentity,
  resolveTeammateAgentConfigSettings,
  TeammateAgentBranch,
  teammateAgentConfigSettingsSchema,
  TeammateAgentKind,
  type AdminApiTeammateAgent,
  type StaticTeammateAgentIdentity,
  type StaticTeammateAgentSections,
  type TeammateAgentConfigSettings,
  type TeammateAgentIdentity,
} from "@repo/app-types/entities/teammateAgent";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  useGetTeammateAgentQuery,
  useRevertTeammateAgentConfigBranchMutation,
  useUpdateTeammateAgentConfigMutation,
} from "../api/entities/teammateAgent.js";

export interface TeammateAgentConfigEditorData {
  identityName: string;
  identityRole: string;
  identityAdditionalContext: string;
  toolIds: readonly string[];
  /**
   * Authored prompt sections for static agents. `null` for legacy
   * (playbook-generated) agents, which don't have sections.
   */
  sections: StaticTeammateAgentSections | null;
  /**
   * Calling hours, retry policy and inbound handling. Whether the agent may
   * dial at all isn't in here — that's the kill switch, which applies
   * immediately rather than waiting for a publish.
   */
  settings: TeammateAgentConfigSettings;
}

/** Describes which agent config fields have been changed locally */
export interface TeammateAgentConfigChanges {
  identityNameChanged: boolean;
  identityRoleChanged: boolean;
  identityAdditionalContextChanged: boolean;
  toolIdsChanged: boolean;
  instructionSectionsChanged: boolean;
  settingsChanged: boolean;
}

/** Validation error messages for agent config fields */
export interface TeammateAgentConfigValidationErrors {
  identityName: string | null;
  identityRole: string | null;
  identityAdditionalContext: string | null;
  callingHours: string | null;
  inboundForwardTo: string | null;
}

export interface UseTeammateAgentConfigEditorResult {
  /** The current agent config data (draft + local changes), undefined while loading */
  data: TeammateAgentConfigEditorData | undefined;

  /** The draft agent config data (before local changes), undefined while loading */
  initialData: TeammateAgentConfigEditorData | undefined;

  /** The live agent config data (for publish comparison), undefined while loading */
  liveData: TeammateAgentConfigEditorData | undefined;

  /** True while fetching the agent config */
  isLoading: boolean;

  /** True while saving changes (autosave, publish, or revert) */
  isSaving: boolean;

  /** True if there are unsaved changes (vs draft revision) */
  hasUnsavedChanges: boolean;

  /** True if current data differs from live (anything to publish) */
  hasUnpublishedChanges: boolean;

  /** True if the current data is valid (can be saved) */
  isValid: boolean;

  /**
   * Why the current data can't be published, or null when it can. Separate
   * from `isValid`, which gates autosave: a draft is allowed to be
   * half-finished, but what goes live has to be coherent.
   */
  publishBlockedReason: string | null;

  /** True if the agent is a static (authored-sections) agent */
  isStatic: boolean;

  /** True if the agent may place outbound calls (applies immediately) */
  isOutboundEnabled: boolean;

  /** Validation errors for each field, null if field is valid */
  validationErrors: TeammateAgentConfigValidationErrors;

  /** Describes which specific fields have changed locally (vs draft) */
  changes: TeammateAgentConfigChanges;

  /** Describes which specific fields differ from live (for publish preview) */
  unpublishedChanges: TeammateAgentConfigChanges;

  /** ISO timestamp of when the draft was last saved, null if no draft */
  lastSavedAt: string | null;

  /** The ID of the current draft revision, used to compare against test runs */
  draftRevisionId: string | null;

  /** Apply partial changes to the data */
  onChange: (changes: Partial<TeammateAgentConfigEditorData>) => void;

  /** Discard all changes and revert draft to live (async) */
  onDiscardChanges: () => Promise<void>;

  /** Publish changes (promotes draft to live) */
  onPublishChanges: () => Promise<void>;

  /** Save changes to draft (used for autosave) */
  onSaveDraft: () => Promise<void>;
}

/**
 * Returns true if any field has changed.
 */
function computeHasAnyChanges(changes: TeammateAgentConfigChanges): boolean {
  return (
    changes.identityNameChanged ||
    changes.identityRoleChanged ||
    changes.identityAdditionalContextChanged ||
    changes.toolIdsChanged ||
    changes.instructionSectionsChanged ||
    changes.settingsChanged
  );
}

const NO_VALIDATION_ERRORS: TeammateAgentConfigValidationErrors = {
  identityName: null,
  identityRole: null,
  identityAdditionalContext: null,
  callingHours: null,
  inboundForwardTo: null,
};

/**
 * Advisory messages for the Configuration tab's settings.
 *
 * Deliberately NOT part of {@link computeIsValid}. One editor backs every tab,
 * and `isValid` gates autosave for all of it — so blocking on these would stop
 * prompt edits from saving while the user is on a tab that can't even show the
 * reason. Every state described here is one the schema accepts, so it saves;
 * the message just explains what won't work until it's fixed.
 */
function computeSettingsWarnings(
  settings: TeammateAgentConfigSettings,
  isOutboundEnabled: boolean,
): Pick<
  TeammateAgentConfigValidationErrors,
  "callingHours" | "inboundForwardTo"
> {
  const hasCallingWindow = Object.values(settings.calling?.hours ?? {}).some(
    (windows) => windows.length > 0,
  );

  return {
    // With no window there is no time to schedule, and the server fails call
    // tasks with `no_calling_window` rather than deferring them forever.
    callingHours:
      isOutboundEnabled && !hasCallingWindow
        ? "Turn on at least one day, or outbound calls can't be scheduled."
        : null,
    inboundForwardTo:
      settings.inbound?.mode === "forward" && !settings.inbound.forward_to
        ? "Add a number, or inbound calls keep going to the agent."
        : null,
  };
}

/**
 * Validates agent config data and returns validation errors.
 * Returns null for valid fields, error message for invalid fields.
 */
function computeValidationErrors(
  data: TeammateAgentConfigEditorData | undefined,
  isStaticAgent: boolean,
  isOutboundEnabled: boolean,
): TeammateAgentConfigValidationErrors {
  if (!data) {
    return NO_VALIDATION_ERRORS;
  }

  // Static agents don't use the persona identity fields: this editor neither
  // edits nor submits them (they're managed by Alpharun), so identity
  // validation doesn't apply.
  return {
    identityName:
      !isStaticAgent && data.identityName.trim() === ""
        ? "Agent name is required"
        : null,
    identityRole:
      !isStaticAgent && data.identityRole.trim() === ""
        ? "Agent role is required"
        : null,
    identityAdditionalContext:
      !isStaticAgent && data.identityAdditionalContext.trim() === ""
        ? "Customer description is required"
        : null,
    ...computeSettingsWarnings(data.settings, isOutboundEnabled),
  };
}

/**
 * Returns true if there are no blocking validation errors. The settings
 * messages are advisory (see {@link computeSettingsWarnings}) and don't count.
 */
function computeIsValid(errors: TeammateAgentConfigValidationErrors): boolean {
  return (
    errors.identityName === null &&
    errors.identityRole === null &&
    errors.identityAdditionalContext === null
  );
}

/**
 * Computes which fields have changed between two agent config data objects.
 */
function computeChanges(
  current: TeammateAgentConfigEditorData | undefined,
  baseline: TeammateAgentConfigEditorData | undefined,
): TeammateAgentConfigChanges {
  if (!baseline || !current) {
    return {
      identityNameChanged: false,
      identityRoleChanged: false,
      identityAdditionalContextChanged: false,
      toolIdsChanged: false,
      instructionSectionsChanged: false,
      settingsChanged: false,
    };
  }

  return {
    identityNameChanged: current.identityName !== baseline.identityName,
    identityRoleChanged: current.identityRole !== baseline.identityRole,
    identityAdditionalContextChanged:
      current.identityAdditionalContext !== baseline.identityAdditionalContext,
    toolIdsChanged:
      JSON.stringify([...current.toolIds].sort()) !==
      JSON.stringify([...baseline.toolIds].sort()),
    // Compared as they'd be stored: the server trims sections when it parses
    // them, so trailing whitespace is not a change any save could persist.
    instructionSectionsChanged: !compareStaticSectionsAsStored(
      current.sections,
      baseline.sections,
    ),
    settingsChanged:
      serializeSettingsForComparison(current.settings) !==
      serializeSettingsForComparison(baseline.settings),
  };
}

/**
 * A key-order-independent rendering of the settings the editor owns, as the
 * server would store them: a control that rebuilds an object doesn't read as a
 * change, and neither does a difference the schema's normalization erases (it
 * reformats a complete forwarding number to E.164 on the way in). Half-typed
 * settings don't parse; those compare as they were entered.
 */
function serializeSettingsForComparison(
  settings: TeammateAgentConfigSettings,
): string {
  const parsed = teammateAgentConfigSettingsSchema.safeParse(settings);
  const stored = parsed.success ? parsed.data : settings;

  return JSON.stringify({
    hours: allCallingWeekdays.map((weekday) => [
      weekday,
      stored.calling?.hours?.[weekday] ?? [],
    ]),
    retry: stored.calling?.retry ?? null,
    timezone: stored.calling?.timezone ?? null,
    agentManaged: stored.calling?.agent_managed ?? false,
    inboundMode: stored.inbound?.mode ?? null,
    inboundForwardTo: stored.inbound?.forward_to ?? null,
    autoEnrollment: stored.auto_enrollment ?? null,
  });
}

/**
 * The local edits still worth holding on to once a save has landed: the ones
 * the server stored differently, because it normalizes as it parses (sections
 * are trimmed, a complete forwarding number is reformatted to E.164). Handing
 * those back to the saved draft would rewrite the field being edited — eating
 * the character just typed and taking the browser's undo history with it.
 *
 * Safe to keep: {@link computeChanges} compares both as they'd be stored, so
 * what is kept here doesn't read as an unsaved change or re-arm autosave.
 */
function retainLocallyDivergentChanges(
  current: Partial<TeammateAgentConfigEditorData> | undefined,
  saved: TeammateAgentConfigEditorData,
): Partial<TeammateAgentConfigEditorData> | undefined {
  const retained: Partial<TeammateAgentConfigEditorData> = {};

  if (
    current?.sections &&
    JSON.stringify(current.sections) !== JSON.stringify(saved.sections)
  ) {
    retained.sections = current.sections;
  }
  if (
    current?.settings &&
    JSON.stringify(current.settings) !== JSON.stringify(saved.settings)
  ) {
    retained.settings = current.settings;
  }

  return Object.keys(retained).length > 0 ? retained : undefined;
}

/**
 * Converts an API teammate agent response to TeammateAgentConfigEditorData.
 */
function toTeammateAgentConfigEditorData(
  teammateAgent: AdminApiTeammateAgent,
): TeammateAgentConfigEditorData {
  const identity = teammateAgent.teammate_agent_config.identity;
  return {
    identityName: identity.name,
    identityRole: identity.role,
    identityAdditionalContext: identity.additional_context,
    toolIds: teammateAgent.teammate_agent_config.tool_ids,
    sections: isStaticTeammateAgentIdentity(identity)
      ? identity.sections
      : null,
    // Re-resolved rather than trusted: during a deploy this bundle can be
    // served against an API that predates the field, and everything below
    // reads `settings.calling` without a guard.
    settings: resolveTeammateAgentConfigSettings(
      teammateAgent.teammate_agent_config.settings,
    ),
  };
}

/**
 * Converts TeammateAgentConfigEditorData to a legacy TeammateAgentIdentity for
 * the API.
 */
function toIdentity(
  data: TeammateAgentConfigEditorData,
): TeammateAgentIdentity {
  return {
    name: data.identityName,
    role: data.identityRole,
    additional_context: data.identityAdditionalContext,
  };
}

/**
 * Builds a static identity from the edited sections, preserving the stored
 * persona fields (kept as empty strings on static agents).
 */
function toStaticIdentity(
  data: TeammateAgentConfigEditorData,
  base: StaticTeammateAgentIdentity,
): StaticTeammateAgentIdentity {
  return {
    name: base.name,
    role: base.role,
    additional_context: base.additional_context,
    kind: TeammateAgentKind.Static,
    sections: data.sections ?? base.sections,
  };
}

/**
 * Computes the identity payload for a config write: a static identity carrying
 * the edited sections for static agents, the legacy persona identity otherwise.
 */
function toIdentityPayload(
  data: TeammateAgentConfigEditorData,
  currentIdentity: TeammateAgentIdentity | undefined,
): TeammateAgentIdentity {
  if (currentIdentity && isStaticTeammateAgentIdentity(currentIdentity)) {
    return toStaticIdentity(data, currentIdentity);
  }
  return toIdentity(data);
}

/**
 * Hook that manages the lifecycle and state of teammate agent config editing.
 * Handles fetching, change tracking, discarding changes, and saving.
 *
 * - Fetches both live and draft revisions
 * - Tracks local changes against the draft
 * - Supports autosave to draft and publish to live
 *
 * @param teammateAgentId - The ID of the teammate agent to edit
 */
export function useTeammateAgentConfigEditor(
  teammateAgentId: string | null,
): UseTeammateAgentConfigEditorResult {
  // Fetch live revision
  const {
    data: fetchedLiveAgent,
    isLoading: isLoadingLive,
    isFetching: isFetchingLive,
  } = useGetTeammateAgentQuery(
    teammateAgentId
      ? { id: teammateAgentId, branch: TeammateAgentBranch.Live }
      : skipToken,
  );

  // Fetch draft revision
  const {
    data: fetchedDraftAgent,
    isLoading: isLoadingDraft,
    isFetching: isFetchingDraft,
  } = useGetTeammateAgentQuery(
    teammateAgentId
      ? { id: teammateAgentId, branch: TeammateAgentBranch.Draft }
      : skipToken,
  );

  // Mutations
  const [updateAgentConfig, { isLoading: isUpdating }] =
    useUpdateTeammateAgentConfigMutation();
  const [revertAgentConfigBranch, { isLoading: isReverting }] =
    useRevertTeammateAgentConfigBranchMutation();

  // Track local changes as a partial object (undefined = no changes)
  const [changedData, setChangedData] =
    useState<Partial<TeammateAgentConfigEditorData>>();

  // Track previous agentId to reset changes in render phase when it changes
  const prevAgentIdRef = useRef(teammateAgentId);
  if (prevAgentIdRef.current !== teammateAgentId) {
    prevAgentIdRef.current = teammateAgentId;
    setChangedData(undefined);
  }

  // Derive liveData from live revision
  const liveData: TeammateAgentConfigEditorData | undefined = useMemo(() => {
    if (!fetchedLiveAgent) {
      return undefined;
    }

    return toTeammateAgentConfigEditorData(fetchedLiveAgent);
  }, [fetchedLiveAgent]);

  // Derive initialData from draft revision (what we compare local changes against)
  const initialData: TeammateAgentConfigEditorData | undefined = useMemo(() => {
    if (!fetchedDraftAgent) {
      return undefined;
    }

    return toTeammateAgentConfigEditorData(fetchedDraftAgent);
  }, [fetchedDraftAgent]);

  // Compute current data by merging draft data with local changes
  const data: TeammateAgentConfigEditorData | undefined = useMemo(() => {
    if (!initialData) {
      return undefined;
    }

    if (!changedData) {
      return initialData;
    }

    return {
      ...initialData,
      ...changedData,
    };
  }, [initialData, changedData]);

  // Calculate changes vs draft (for tracking unsaved changes)
  const changes: TeammateAgentConfigChanges = useMemo(
    () => computeChanges(data, initialData),
    [data, initialData],
  );

  // Calculate unpublished changes (current data vs live)
  const unpublishedChanges: TeammateAgentConfigChanges = useMemo(
    () => computeChanges(data, liveData),
    [data, liveData],
  );

  const hasUnsavedChanges = computeHasAnyChanges(changes);
  const hasUnpublishedChanges = computeHasAnyChanges(unpublishedChanges);

  const currentIdentity = (fetchedDraftAgent ?? fetchedLiveAgent)
    ?.teammate_agent_config.identity;
  const isStaticAgent =
    currentIdentity !== undefined &&
    isStaticTeammateAgentIdentity(currentIdentity);

  // Not branch-dependent: the kill switch writes straight through, so both
  // branches report the same, always-live value.
  const isOutboundEnabled =
    fetchedLiveAgent?.teammate_agent_config.outbound_calling_enabled ?? false;

  // Compute validation errors
  const validationErrors = useMemo(
    () => computeValidationErrors(data, isStaticAgent, isOutboundEnabled),
    [data, isStaticAgent, isOutboundEnabled],
  );
  const isValid = computeIsValid(validationErrors);
  const publishBlockedReason = data
    ? getUnpublishableSettingsReason(data.settings)
    : null;

  // lastSavedAt and draftRevisionId from draft revision
  const lastSavedAt = fetchedDraftAgent?.revision_created_at ?? null;
  const draftRevisionId =
    fetchedDraftAgent?.teammate_agent_config_revision_id ?? null;

  // onChange handler - merge partial changes
  const onChange = useCallback(
    (newChangedData: Partial<TeammateAgentConfigEditorData>) => {
      setChangedData((prev) => ({
        ...prev,
        ...newChangedData,
      }));
    },
    [],
  );

  // onSaveDraft handler - save current data to draft branch
  const onSaveDraft = useCallback(async (): Promise<void> => {
    if (!data || !teammateAgentId) {
      return;
    }

    const savedAgent = await updateAgentConfig({
      teammateAgentId,
      branch: TeammateAgentBranch.Draft,
      payload: {
        // Static agents submit a static identity carrying the edited sections
        // (server rebuilds the prompt synchronously); legacy agents submit the
        // persona identity.
        identity: toIdentityPayload(data, currentIdentity),
        tool_ids: [...data.toolIds],
        // Only when actually edited: sending the server-resolved defaults back
        // on every save would materialize settings onto agents nobody has
        // configured, freezing them against future default changes.
        ...(changes.settingsChanged ? { settings: data.settings } : {}),
      },
    }).unwrap();

    const savedData = toTeammateAgentConfigEditorData(savedAgent);

    // Only clear local changes if user didn't make more changes during the save.
    // This prevents cursor jumping when user types during an in-flight save.
    // The closure captures changedData as it was when the save was called.
    setChangedData((current) => {
      // If user made changes during the save, keep the new changes
      if (JSON.stringify(current) !== JSON.stringify(changedData)) {
        return current;
      }
      // Otherwise safe to clear, except for what the server normalized as it
      // stored it — that would land in the field being edited.
      return retainLocallyDivergentChanges(current, savedData);
    });
  }, [
    data,
    teammateAgentId,
    currentIdentity,
    updateAgentConfig,
    changes.settingsChanged,
    changedData,
  ]);

  // onDiscardChanges handler - revert draft to live and clear local changes
  const onDiscardChanges = useCallback(async (): Promise<void> => {
    // Clear local changes
    setChangedData(undefined);

    // Revert draft to live
    if (teammateAgentId) {
      await revertAgentConfigBranch({
        teammateAgentId,
        branch: TeammateAgentBranch.Draft,
      }).unwrap();
    }
  }, [teammateAgentId, revertAgentConfigBranch]);

  // onPublishChanges handler - save to live branch
  const onPublishChanges = useCallback(async (): Promise<void> => {
    if (!data || !teammateAgentId) {
      return;
    }

    await updateAgentConfig({
      teammateAgentId,
      branch: TeammateAgentBranch.Live,
      payload: {
        // Static agents submit a static identity carrying the edited sections
        // (server rebuilds the prompt synchronously); legacy agents submit the
        // persona identity.
        identity: toIdentityPayload(data, currentIdentity),
        tool_ids: [...data.toolIds],
        // Only when actually edited: sending the server-resolved defaults back
        // on every save would materialize settings onto agents nobody has
        // configured, freezing them against future default changes.
        ...(unpublishedChanges.settingsChanged
          ? { settings: data.settings }
          : {}),
      },
    }).unwrap();

    // Clear local changes on success
    setChangedData(undefined);
  }, [
    data,
    teammateAgentId,
    currentIdentity,
    updateAgentConfig,
    unpublishedChanges.settingsChanged,
  ]);

  // Determine loading state
  const isLoading =
    teammateAgentId !== null &&
    (isLoadingLive || isFetchingLive || isLoadingDraft || isFetchingDraft);
  const isSaving = isUpdating || isReverting;

  return {
    data,
    initialData,
    liveData,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    hasUnpublishedChanges,
    isValid,
    publishBlockedReason,
    isStatic: isStaticAgent,
    isOutboundEnabled,
    validationErrors,
    changes,
    unpublishedChanges,
    lastSavedAt,
    draftRevisionId,
    onChange,
    onDiscardChanges,
    onPublishChanges,
    onSaveDraft,
  };
}
